import { EndUserInboxStatusMapping, ThreadCategoryStatusEnum, ThreadStatusEnum, TicketCategoryStatusEnum } from "@/utils/constants";
import { fetchJSON } from "./utils";
import { useUserStore } from "@/stores/user";
import { getSortParams } from "@/utils/utils";

// API to call thread BE routes
const BASE_THREADS_URL = "/api/v2/threads";
const BASE_THREAD_URL = "/api/v2/thread";

export const getThread = (id) => {
  const userStore = useUserStore();
  const url = `${BASE_THREAD_URL}/${id}`;
  const params = { organization_id: userStore.authUserOrganizationId };

  return fetchJSON(url, params, null, "get");
};

export const getThreadForId = (unknownId) => {
  const userStore = useUserStore();
  const url = `${BASE_THREAD_URL}/for/${unknownId}`;
  const params = { organization_id: userStore.authUserOrganizationId };

  return fetchJSON(url, params, null, "get");
}

export const getThreads = (filters = {}) => {
  const userStore = useUserStore();
  const url = `${BASE_THREADS_URL}`;
  const params = {
    organization_id: userStore.authUserOrganizationId,
    fetch_links: true,
    ...filters,
  };

  // replace the FE only query params with the correct BE arguments
  _cleanFetchQueryParams(params)
  return fetchJSON(url, params, null, "get");
};

export const patchThread = (id, payload = {}) => {
  const url = `${BASE_THREAD_URL}/${id}`
  return fetchJSON(url, null, payload, "PATCH")
}

export const patchSeenBy = async (id, payload = true) => {
  const url = `${BASE_THREAD_URL}/${id}/seen`
  return fetchJSON(url, null, {seen: payload}, "PATCH")
}

export const patchTags = async (id, tag) => {
  const url = `${BASE_THREAD_URL}/${id}/tag`
  return fetchJSON(url, null, { tag }, "PATCH")
}

export const patchTicketPriority = async (id, ticketPriorityId) => {
  const url = `${BASE_THREAD_URL}/${id}/ticket-priority`
  return fetchJSON(url, null, { ticket_priority_id: ticketPriorityId }, "PATCH")
}

export const patchUnreadBy = async (id, payload) => {
  const url = `${BASE_THREAD_URL}/${id}/unread`
  return fetchJSON(url, null, {unread: payload}, "PATCH")
}

// changes a thread to a ticket,
// sets the thread_dfa_state to closed and ticket_status to ticket
export const patchToTicket = async (id) => {
  const url = `${BASE_THREAD_URL}/${id}/create-ticket`
  return fetchJSON(url, null, null, "POST")
}

const asArray = (v) => {
  if (v == null) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === "string") return v.split(",").map(s => s.trim()).filter(Boolean);
  return [v];
};

const SIDEBAR_FILTER_TO_API = {
  sidebar_filter_created_start_date: "created_at_from",
  sidebar_filter_created_end_date: "created_at_to",
  sidebar_filter_dt_user_responded_start_date: "dt_user_responded_from",
  sidebar_filter_dt_user_responded_end_date: "dt_user_responded_to",
  sidebar_filter_user_id: "user_id",
  sidebar_filter_assignee: "assignees",
  sidebar_filter_dt_open_start_date: "dt_ticket_open_from",
  sidebar_filter_dt_open_end_date: "dt_ticket_open_to",
  sidebar_filter_dt_closed_start_date: "dt_ticket_closed_from",
  sidebar_filter_dt_closed_end_date: "dt_ticket_closed_to",
  sidebar_filter_last_modified_start_date: "updated_at_from",
  sidebar_filter_last_modified_end_date: "updated_at_to",
  sidebar_filter_team: "teams",
  sidebar_filter_ticket_priority: "ticket_priority_ids",
};

const _applySidebarFiltersToQueryParams = (queryParams) => {
  Object.keys(queryParams).forEach((key) => {
    if (!key.startsWith("sidebar_filter_")) return;
    const apiKey = SIDEBAR_FILTER_TO_API[key];
    if (apiKey != null) {
      const value = queryParams[key];
      if (value !== null && value !== undefined) {
        queryParams[apiKey] = value;
      }
    }
    delete queryParams[key];
  });
};

const _hasSidebarFilterValue = (value) => {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.trim() !== "";
  return true;
};

/** Inbox category enum value for "My Tickets" - used only for request cleaning. */
const INBOX_CATEGORY_MY_TICKETS = "MY_TICKETS";

const _cleanFetchQueryParams = (queryParams) => {
  const inboxCategory = queryParams._inbox_category;
  delete queryParams._inbox_category;

  const hadSidebarAssignee = _hasSidebarFilterValue(queryParams.sidebar_filter_assignee);
  const hadSidebarTeam = _hasSidebarFilterValue(queryParams.sidebar_filter_team);

  _applySidebarFiltersToQueryParams(queryParams);

  // On "My Tickets" page: sidebar assignee/team should be the only assignee/team filter (override category defaults).
  if (inboxCategory === INBOX_CATEGORY_MY_TICKETS) {
    // If BOTH are selected in the sidebar, we keep both (they already override defaults via mapping).
    // If only one is selected, remove the other default filter to avoid unintended combined logic.
    if (hadSidebarAssignee && !hadSidebarTeam) delete queryParams.teams;
    if (hadSidebarTeam && !hadSidebarAssignee) delete queryParams.assignees;
  }

  queryParams.status = asArray(queryParams.status);
  queryParams.user_inbox_status = asArray(queryParams.user_inbox_status)
  // replace the status = Tickets with the correct BE arguments
  if(queryParams.status?.includes(ThreadCategoryStatusEnum.TICKETS)) {
    queryParams.status = queryParams.status.filter(s => s !== ThreadCategoryStatusEnum.TICKETS)
    queryParams.status = [...queryParams.status, ...Object.values(TicketCategoryStatusEnum)]
  }

  if(queryParams.user_inbox_status.length) {
    // Replace for end user inbox
    const mappedStatuses = queryParams.user_inbox_status.flatMap(status => EndUserInboxStatusMapping[status] ?? status)

    // only add the unique values
    queryParams.status = [...new Set([...mappedStatuses, ...queryParams.status])]

    // remove user_inbox_status before sending to api
    delete queryParams.user_inbox_status
  }

  // replace the sort FE param with the correct BE arguments
  if(queryParams.sort_option) {
    const { sort, order } = getSortParams(queryParams.sort_option)
    queryParams.sort = sort
    queryParams.order = order
    delete queryParams.sort_option
  }


  if(!queryParams.status.length) delete queryParams.status
}

export default {
  patchThread,
  patchSeenBy,
  patchTags,
  patchTicketPriority,
  patchUnreadBy,
  patchToTicket,
  getThread,
  getThreadForId,
  getThreads,
};
